import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, LogIn, RefreshCw, WifiOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ApiContext = createContext(null);

export const useApi = () => {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used within ApiProvider');
  return ctx;
};

/**
 * ApiProvider — wraps API calls with:
 *  1. Automatic Clerk token injection
 *  2. 401 detection → session expired banner
 *  3. Network offline detection
 */
export const ApiProvider = ({ children }) => {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [sessionExpired, setSessionExpired] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);

  // Track online/offline
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    if (!navigator.onLine) {
      throw new Error('You appear to be offline. Check your connection and try again.');
    }

    let token;
    try {
      token = await getToken();
    } catch (e) {
      setSessionExpired(true);
      throw new Error('Session expired');
    }

    if (!token) {
      setSessionExpired(true);
      throw new Error('Session expired');
    }

    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (res.status === 401) {
      setSessionExpired(true);
      throw new Error('Session expired');
    }

    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.error || `HTTP ${res.status}`);
      if (res.status >= 500) {
        Sentry.captureException(err, { tags: { apiPath: path, statusCode: res.status } });
      }
      throw err;
    }
    return data;
  }, [getToken]);

  const handleReSignIn = () => {
    signOut().then(() => {
      window.location.href = '/sign-in';
    });
  };

  const handleRetry = () => {
    setSessionExpired(false);
  };

  return (
    <ApiContext.Provider value={{ apiFetch, sessionExpired, offline }}>
      {children}

      {/* Session expired banner */}
      {sessionExpired && (
        <div className="fixed inset-x-0 bottom-0 z-[70] bg-amber-50 border-t border-amber-200 px-4 py-3 shadow-lg animate-slide-up">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900">Your session has expired</p>
              <p className="text-xs text-amber-600">Sign in again to continue where you left off.</p>
            </div>
            <button onClick={handleRetry} className="btn btn-ghost text-xs flex-shrink-0">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Retry
            </button>
            <button onClick={handleReSignIn} className="btn btn-primary text-xs flex-shrink-0">
              <LogIn className="w-3.5 h-3.5 mr-1.5" />
              Sign in
            </button>
          </div>
        </div>
      )}

      {/* Offline banner */}
      {offline && (
        <div className="fixed inset-x-0 bottom-0 z-[70] bg-cayda-900 px-4 py-3 animate-slide-up">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <WifiOff className="w-5 h-5 text-cayda-400 flex-shrink-0" />
            <p className="text-sm text-cayda-300">
              You're offline. Changes won't save until you reconnect.
            </p>
          </div>
        </div>
      )}
    </ApiContext.Provider>
  );
};
